import {
  BALCONY_TEMPLATE_FIELDS,
  getAllScopeSectionsCatalog,
  getVisibleScopeSections,
  emptyBalconyInstance,
} from './scopeSheetSchema';

const defaultForField = (field) => {
  switch (field.type) {
    case 'checkboxGroup':
      return [];
    case 'notes':
    case 'richNotes':
      return { format: 'tiptap', doc: { type: 'doc', content: [{ type: 'paragraph' }] } };
    case 'text':
    case 'number':
    case 'singleSelect':
    case 'yesNo':
    default:
      return '';
  }
};

const emptySubsection = (subsection) => {
  const values = {};
  (subsection.fields || []).forEach((field) => {
    values[field.id] = defaultForField(field);
  });
  return values;
};

const emptySection = (section) => {
  if (section.type === 'balconyList') {
    return { balconies: [emptyBalconyInstance()] };
  }
  const data = {};
  (section.subsections || []).forEach((subsection) => {
    data[subsection.id] = emptySubsection(subsection);
  });
  return data;
};

export const emptyScopeSheet = () => {
  const sheet = {};
  getAllScopeSectionsCatalog().forEach((section) => {
    sheet[section.id] = emptySection(section);
  });
  return sheet;
};

export const emptyBalcony = () => emptyBalconyInstance();

const isFilledValue = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === 'object' && value.format === 'tiptap') {
    const plain = JSON.stringify(value.doc || {})
      .replace(/"type":"paragraph"/g, '')
      .replace(/"type":"doc"/g, '')
      .replace(/[{}\[\]",:]/g, '')
      .replace(/content/g, '')
      .replace(/text/g, '')
      .trim();
    return plain.length > 0;
  }
  if (typeof value === 'string') {
    const plain = value
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return plain.length > 0;
  }
  if (typeof value === 'number') return !Number.isNaN(value);
  if (value && typeof value === 'object') {
    return Object.values(value).some(isFilledValue);
  }
  return false;
};

export const isSectionFilled = (sectionId, scopeSheet) => {
  const data = scopeSheet?.[sectionId];
  if (!data) return false;
  return isFilledValue(data);
};

export const countFilledSections = (scopeSheet) => {
  const visible = getVisibleScopeSections(scopeSheet);
  return visible.reduce(
    (count, section) =>
      count + (isSectionFilled(section.id, scopeSheet) ? 1 : 0),
    0
  );
};

export const deriveProjectMetaFromScope = (scopeSheet = {}) => {
  const basic = scopeSheet?.A?.basicInfo || {};
  const clientName = String(basic.clientNames || '').trim();
  const location = String(basic.projectAddress || '').trim();
  const name = location || clientName || 'Untitled Project';
  return { name, clientName, location };
};

/** Migrate older section keys (I/J/K/L/M/N) into stable ids. */
export const migrateScopeSheetKeys = (incoming) => {
  if (!incoming || typeof incoming !== 'object') return incoming;
  const next = { ...incoming };
  const map = {
    I: 'bedroom-1',
    J: 'bedroom-2',
    K: 'bedroom-3',
    L: 'help-room',
    M: 'store-room',
    N: 'balconies',
  };
  Object.entries(map).forEach(([oldKey, newKey]) => {
    if (next[oldKey] != null && next[newKey] == null) {
      next[newKey] = next[oldKey];
    }
  });

  // Legacy unitType checkbox array → single string
  if (next.A?.basicInfo && Array.isArray(next.A.basicInfo.unitType)) {
    next.A = {
      ...next.A,
      basicInfo: {
        ...next.A.basicInfo,
        unitType: next.A.basicInfo.unitType[0] || '',
      },
    };
  }

  return next;
};

/** Deep-set a field value in scope sheet */
export const setScopeFieldValue = (
  scopeSheet,
  { sectionId, subsectionId, fieldId, value, balconyIndex, balconyGroup }
) => {
  const next = structuredClone(scopeSheet);

  if (sectionId === 'balconies' || sectionId === 'N') {
    const key = 'balconies';
    if (!next[key]) next[key] = { balconies: [emptyBalconyInstance()] };
    if (!Array.isArray(next[key].balconies)) {
      next[key].balconies = [emptyBalconyInstance()];
    }
    const idx = balconyIndex ?? 0;
    while (next[key].balconies.length <= idx) {
      next[key].balconies.push(emptyBalconyInstance());
    }
    if (balconyGroup === 'label') {
      next[key].balconies[idx].label = value;
      return next;
    }
    if (!next[key].balconies[idx][balconyGroup]) {
      next[key].balconies[idx][balconyGroup] = {};
    }
    next[key].balconies[idx][balconyGroup][fieldId] = value;
    return next;
  }

  if (!next[sectionId]) next[sectionId] = {};
  if (!next[sectionId][subsectionId]) next[sectionId][subsectionId] = {};
  next[sectionId][subsectionId][fieldId] = value;
  return next;
};

export const getBalconyGroupTitles = () => ({
  basic: '1. Basic',
  civil: '2. Civil',
  floor: '3. Floor Coverings',
  walls: '4. Wall Coverings',
  ceiling: '5. Ceiling',
  carpentry: '6. Carpentry / Built-In',
  looseFurniture: '7. Loose Furniture',
  electrical: '8. Electrical',
  plumbing: '9. Water / Plumbing',
  greenery: '10. Greenery / Planters',
  safety: '11. Safety',
});

export const getBalconyFieldsForGroup = (groupId) =>
  BALCONY_TEMPLATE_FIELDS[groupId] || [];
